/**
 * Grafana Faro bootstrap for the customer portal SPA.
 *
 * Vendored copy of apps/frontend/src/observability/faro.ts (minus the
 * staff-only performance-wallboard carve-out, which has no equivalent
 * route here) — see ../app/components/primitives/README.md for why the two
 * shells duplicate rather than share their web-only plumbing.
 *
 * One-time initialization of the Faro Web SDK. Captures:
 *   - unhandled JS errors / promise rejections
 *   - React error-boundary failures (if present)
 *   - Core Web Vitals + page-load timings
 *   - distributed traces (via `@grafana/faro-web-tracing`), with
 *     `traceparent` injected into outbound `fetch` calls to the
 *     API so the frontend span joins the backend span in Tempo.
 *
 * Signals ship via the Grafana Cloud Frontend Observability
 * ingest URL. Alloy is not involved on this path — Faro posts
 * directly to Grafana Cloud over HTTPS. CORS / auth are handled
 * by the collector URL's own token.
 *
 * Safe-by-construction:
 *   - Returns immediately if `faroEnabled` is not set in the runtime config.
 *   - Returns immediately if `faroCollectorUrl` is empty.
 *   - The caller wraps the whole invocation in try/catch so any
 *     SDK-level failure prints to `console.error` but never
 *     breaks the application bootstrap.
 *
 * House style for the frontend logging convention is documented in
 * `.claude/rules/logging.md`. tl;dr: use stable event names like
 * `customers.list.failed`, pass identifiers via the attributes
 * object, never interpolate them into the message string.
 */

import { initializeFaro, getWebInstrumentations } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";

const APP_NAME = "customer-intelligence-customer-portal";

/** The customer's quote and order pages by link: their token is the credential and sits in the path, so Faro
 *  (which records page and fetch URLs) is not started for a session that begins on one. */
export function isTokenLinkPath(pathname: string): boolean {
  return /^\/(quote|order)\//.test(pathname);
}

export function initFaro(): void {
  // Read inside the function, not at module scope: configuration resolves
  // during bootstrap and this module sits in main.tsx's static import graph.
  const {
    faroEnabled,
    faroCollectorUrl: COLLECTOR_URL,
    appVersion: APP_VERSION,
    environment: ENVIRONMENT,
    apiBaseUrl: API_BASE_URL,
  } = getRuntimeConfig();

  if (!faroEnabled) return;
  if (!COLLECTOR_URL) {
    console.warn(
      "[faro] faroEnabled is set but the collector URL is empty; skipping init.",
    );
    return;
  }

  // Propagate `traceparent` only to the API base URL so we never
  // leak trace context to third-party services. The regex matches
  // any request whose URL starts with the configured base.
  const propagateTargets: (string | RegExp)[] = API_BASE_URL
    ? [new RegExp(`^${escapeRegExp(API_BASE_URL)}`)]
    : [];

  initializeFaro({
    url: COLLECTOR_URL,
    app: {
      name: APP_NAME,
      version: APP_VERSION,
      environment: ENVIRONMENT,
    },
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: propagateTargets,
        },
      }),
    ],
  });
}

function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
